// src/components/auth/LoginForm.tsx
import { useState } from 'react';
import {
  Paper,
  Title,
  Container,
  Stack,
  LoadingOverlay,
  Alert,
  Text,
  Grid,
  List,
  Group,
  Anchor
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconBrandWindows, IconLifebuoy } from '@tabler/icons-react';
import { authApi } from '../../api/auth';
import { ActionButton } from '../common/ActionButton';
import { Link } from 'react-router-dom';

export function LoginForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await authApi.login();
      // This won't execute immediately due to redirect
      setIsLoading(false);
    } catch (error) {
      console.error("Login failed in component:", error);
      
      // Extract the most useful error message
      let errorMessage = 'Authentication failed. Please try again.';
      
      if (error instanceof Error) {
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
        
        // Check for specific MSAL initialization error
        if (error.message.includes('uninitialized_public_client_application')) {
          errorMessage = 'The authentication service is initializing. Please try again in a few moments.';
        } else if (error.message.includes('interaction_in_progress')) {
          errorMessage = 'Another authentication operation is in progress. Please wait.';
        }
      } else {
        console.error("Unknown error type:", typeof error);
      }
      
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <Container 
      fluid
      sx={(theme) => ({ 
        height: 'calc(100vh - 120px)',
        marginTop: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '@media (max-width: 768px)': {
          height: 'auto',
          minHeight: 'calc(100vh - 120px)',
          alignItems: 'flex-start',
          paddingTop: theme.spacing.xl,
          paddingBottom: theme.spacing.xl,
          paddingLeft: 0,
          paddingRight: 0
        }
      })}
      role="main"
      aria-label="Login page"
    >
      <Grid 
        gutter={0} 
        sx={(theme) => ({ 
          width: '100%', 
          maxWidth: 1200,
          '@media (max-width: 768px)': {
            maxWidth: '100%',
            width: '100%'
          }
        })}
      >
        {/* Welcome Message Column */}
        <Grid.Col 
          span={6}
          md={6}
          sm={12}
          xs={12}
          sx={(theme) => ({
            '@media (max-width: 768px)': {
              maxWidth: '100%',
              flex: '0 0 100%'
            }
          })}
        >
          <Stack 
            spacing="xl" 
            sx={(theme) => ({
              padding: 60,
              '@media (max-width: 768px)': {
                padding: theme.spacing.md,
                paddingTop: 0,
                width: '100%'
              }
            })}
          >
            <div>
              <Title 
                order={1} 
                mb="md"
                sx={(theme) => ({
                  '@media (max-width: 768px)': {
                    fontSize: theme.fontSizes.xl,
                    textAlign: 'center'
                  }
                })}
              >
                Welcome to the Performance Management Portal
              </Title>
              <Text 
                size="lg" 
                color="dimmed" 
                mb="xl"
                sx={(theme) => ({
                  '@media (max-width: 768px)': {
                    display: 'none'
                  }
                })}
              >
                A comprehensive tool for managing and tracking quarterly reports and milestones.
              </Text>
            </div>
            
            {/* Mobile Login Form - only visible on small screens */}
            <Paper 
              withBorder 
              shadow="md" 
              radius="md"
              sx={(theme) => ({
                width: '100%', 
                maxWidth: 400,
                padding: 30,
                margin: '0 auto',
                display: 'none',
                '@media (max-width: 768px)': {
                  display: 'block',
                  padding: theme.spacing.md,
                  maxWidth: '100%',
                  boxShadow: 'none',
                  border: 'none'
                }
              })}
            >
              <Title
                order={2}
                align="center"
                mb="xs"
                sx={(theme) => ({ 
                  fontFamily: `Greycliff CF, ${theme.fontFamily}`,
                  fontWeight: 900 
                })}
              >
                Sign In
              </Title>
              <Text color="dimmed" size="sm" align="center" mb="md">
                Access your dashboard
              </Text>

              {error && (
                <Alert 
                  icon={<IconAlertCircle size={16} />} 
                  title="Error" 
                  color="red" 
                  mb="md"
                  role="alert"
                >
                  {error}
                </Alert>
              )}
              
              <Group 
                position="center" 
                mt="xl"
                sx={(theme) => ({
                  '@media (max-width: 768px)': {
                    marginTop: theme.spacing.lg
                  }
                })}
              >
                <ActionButton 
                  onClick={handleLogin}
                  label="Sign in with Microsoft"
                  ariaLabel="Sign in with Microsoft"
                  actionType="primary"
                  width="240px"
                  leftIcon={<IconBrandWindows size={18} />}
                />
              </Group>
              
              {/* Help link for authentication issues */}
              <Text size="xs" color="dimmed" mt="md" align="center">
                Having trouble logging in?{' '}
                <Anchor component={Link} to="/auth-help" size="xs">
                  <Group spacing={4} display="inline">
                    <IconLifebuoy size={12} />
                    <span>Get help</span>
                  </Group>
                </Anchor>
              </Text>
            </Paper>
            
            <Stack 
              spacing="md"
              sx={(theme) => ({
                '@media (max-width: 768px)': {
                  display: 'none'
                }
              })}
            >
              <Title 
                order={3} 
                mb="sm"
              >
                Key Features:
              </Title>
              <List
                spacing="sm"
                size="md"
                center
                icon={
                  <IconCheck
                    size={16}
                    style={{ marginTop: 4 }}
                  />
                }
              >
                <List.Item>Streamlined quarterly report submissions</List.Item>
                <List.Item>Real-time milestone tracking and updates</List.Item>
                <List.Item>Comprehensive performance analytics</List.Item>
                <List.Item>Secure document management</List.Item>
              </List>
            </Stack>

            <Text 
              size="sm" 
              color="dimmed" 
              mt="xl"
              sx={(theme) => ({
                '@media (max-width: 768px)': {
                  display: 'none'
                }
              })}
            >
              To request access, please contact your initiative lead.
            </Text>
          </Stack>
        </Grid.Col>

        {/* Login Form Column */}
        <Grid.Col 
          span={6}
          md={6}
          sm={0}
          xs={0}
          sx={(theme) => ({
            borderLeft: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
            '@media (max-width: 768px)': {
              display: 'none'
            }
          })}
        >
          <Stack 
            justify="center" 
            align="center" 
            h="100%" 
            spacing="lg"
            sx={(theme) => ({
              padding: 60,
              '@media (max-width: 768px)': {
                padding: theme.spacing.md,
                minHeight: 'auto',
                height: 'auto'
              }
            })}
          >
            <Paper 
              withBorder 
              shadow="md" 
              radius="md"
              sx={(theme) => ({
                width: '100%', 
                maxWidth: 400,
                padding: 30,
                '@media (max-width: 768px)': {
                  padding: theme.spacing.md,
                  maxWidth: '100%',
                  boxShadow: 'none',
                  border: 'none'
                }
              })}
            >
              <Title
                order={2}
                align="center"
                mb="xs"
                sx={(theme) => ({ 
                  fontFamily: `Greycliff CF, ${theme.fontFamily}`,
                  fontWeight: 900 
                })}
              >
                Sign In
              </Title>
              <Text color="dimmed" size="sm" align="center" mb="md">
                Access your dashboard
              </Text>

              {error && (
                <Alert 
                  icon={<IconAlertCircle size={16} />} 
                  title="Error" 
                  color="red" 
                  mb="md"
                  role="alert"
                >
                  {error}
                </Alert>
              )}
              
              <Group 
                position="center" 
                mt="xl"
                sx={(theme) => ({
                  '@media (max-width: 768px)': {
                    marginTop: theme.spacing.lg
                  }
                })}
              >
                <ActionButton 
                  onClick={handleLogin}
                  label="Sign in with Microsoft"
                  ariaLabel="Sign in with Microsoft"
                  actionType="primary"
                  width="240px"
                  leftIcon={<IconBrandWindows size={18} />}
                />
              </Group>
              
              {/* Help link for authentication issues */}
              <Text size="xs" color="dimmed" mt="md" align="center">
                Having trouble logging in?{' '}
                <Anchor component={Link} to="/auth-help" size="xs">
                  <Group spacing={4} display="inline">
                    <IconLifebuoy size={12} />
                    <span>Get help</span>
                  </Group>
                </Anchor>
              </Text>
            </Paper>
          </Stack>
        </Grid.Col>
      </Grid>
      <LoadingOverlay visible={isLoading} />
    </Container>
  );
}